import { Comparator, Predicate } from "@/types/functional";

export function binarySearch<T>(
    comparator: Comparator<T>,
    x: T,
    xs: T[],
): number {
    let min = 0;
    let max = xs.length;

    while (min < max) {
        const midPoint = (min + max) >> 1;
        const res = comparator(x, xs[midPoint]);

        if (res === 0) {
            return midPoint;
        } else if (res < 0) {
            max = midPoint;
        } else {
            min = midPoint + 1;
        }
    }

    return min;
}

export function countIf<T>(f: Predicate<T>, xs: T[]): number {
    return xs.filter(f).length;
}

export function count<T>(a: T, xs: T[]): number {
    return countIf((x) => x === a, xs);
}

export function intersperse<T>(sep: T, xs: T[]): T[] {
    return xs.flatMap((x) => [sep, x]).slice(1);
}

export function unique<T>(xs: T[]): T[] {
    return [...new Set(xs)];
}

export function uniqueBy<TValue, TKey>(
    values: TValue[],
    keySelector: (value: TValue) => TKey,
): TValue[] {
    const map = new Map<TKey, TValue>();

    for (const value of values) {
        const key = keySelector(value);
        if (!map.has(key)) map.set(key, value);
    }

    return [...map.values()];
}

export function sum(xs: number[]): number {
    return xs.reduce((a, b) => a + b, 0);
}

export function zipWith<T>(binOp: (a: T, b: T) => T, a: T[], b: T[]): T[] {
    return a.slice(0, b.length).map((left, i) => binOp(left, b[i]));
}

export function zipReduceWith<T>(binOp: (a: T, b: T) => T, ...xss: T[][]): T[] {
    return xss.reduce((a: T[], b: T[]) => zipWith(binOp, a, b));
}

export function zipReduceSum(...xss: number[][]): number[] {
    return zipReduceWith((a, b) => a + b, ...xss);
}

export function groupBy<T>(relation: (a: T, b: T) => boolean, xs: T[]): T[][] {
    const groups = [] as T[][];
    for (const x of xs) {
        if (groups.length !== 0 && relation(groups[groups.length - 1][0], x)) {
            groups[groups.length - 1].push(x);
        } else {
            groups.push([x]);
        }
    }
    return groups;
}

export function groupOn<T, S>(f: (x: T) => S, xs: T[]): T[][] {
    return groupBy((a, b) => f(a) === f(b), xs);
}

export function groupByX<T>(collections: T[], keySelector: (x: T) => string) {
    return collections.reduce((obj: Record<string, T[]>, item: T) => {
        const key = keySelector(item);
        if (typeof obj[key] === "undefined") {
            obj[key] = [];
        }

        obj[key].push(item);

        return obj;
    }, {});
}

export function takeWhile<T>(f: Predicate<NoInfer<T>>, xs: T[]): T[] {
    const ys = [] as T[];
    for (const x of xs) {
        if (!f(x)) {
            break;
        }

        ys.push(x);
    }
    return ys;
}

export function cumulativeSum(xs: number[]): number[] {
    const ys = Array.from(xs); // deep copy
    for (let i = 1; i < ys.length; i++) ys[i] += ys[i - 1];
    return ys;
}

export function toArray<T>(x: T | T[] | undefined): T[] {
    return Array.isArray(x) ? x : x != null ? [x] : [];
}

export function toSingle<T>(x: T | T[] | undefined): T | undefined {
    return Array.isArray(x) ? x[0] : x;
}

export function cartesianProduct<T, S>(x: T[], y: S[]): [T, S][] {
    return x.flatMap((xv) => y.map((yv) => [xv, yv] as [T, S]));
}

export function shuffle<T>(array: T[]): T[] {
    let currentIndex = array.length;
    let randomIndex: number;

    while (currentIndex !== 0) {
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex--;

        [array[currentIndex], array[randomIndex]] = [
            array[randomIndex],
            array[currentIndex],
        ];
    }

    return array;
}

export function* iterateWhile<T>(
    unaryOp: (val: T) => T,
    cond: (val: T) => boolean,
    initial: T,
): Generator<T> {
    let val = initial;

    while (cond(val)) {
        yield val;
        val = unaryOp(val);
    }
}
